"use client"

import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import {
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography
} from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import VisibilityIcon from "@mui/icons-material/Visibility"
import Box from "@mui/material/Box"
import { AuthContext } from "context"
import "dayjs/locale/it"
import { createMRTColumnHelper } from "material-react-table"
import toast from 'react-hot-toast'
import MDButton from "../../components/MDButton"
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar"
import apiService from "../../services/api-service"
import GenericTable from "../genericTable"
import ExpirationChip from "./expirationChip"

const ConcreteCastingRegistrationManagment = () => {
  const navigate = useNavigate()
  const authContext = React.useContext(AuthContext)
  const [loading, setLoading] = useState(false)
  const [documents, setDocuments] = useState([])
  const [openDialog, setOpenDialog] = useState(false)
  const [openDeliveryDialog, setOpenDeliveryDialog] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedDocumentId, setSelectedDocumentId] = useState(null)

  const columnHelper = createMRTColumnHelper()

  const handleSetNotification = (msg, severityType) => {
    if (severityType === 'success') {
      toast.success(msg, {id: 'concrete-casting-registration-managment-toast'});
    } else {
      toast.error(msg, {id: 'concrete-casting-registration-managment-toast'});
    }
  }

  useEffect(() => {
    fetchDocument()
  }, [])

  const fetchDocument = async () => {
    setLoading(true)
    apiService
      .get(
        "/concrete-casting-registration-managment/",
        { id_area: localStorage.getItem("areaSelected") },
        {},
      )
      .then((response) => {
        if (response) setDocuments(response)
      })
      .catch((error) => {
        handleSetNotification("Errore durante la chiamata al server, codice d'errore #08514262429906", "error")
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const onEdit = (id) => {
    window.location.href = "/concrete-casting-registration-managment/edit/" + id + "/"
  }

  const handleOpenDeliveryDialog = (id) => {
    setSelectedDocumentId(id)
    setOpenDeliveryDialog(true)
  }

  const handleCloseDeliveryDialog = () => {
    setOpenDeliveryDialog(false)
    setSelectedDate(null)
    setSelectedDocumentId(null)
  }

  const handleSaveAttachment = async () => {
    if (!selectedDate || !selectedDocumentId) return

    try {
      // Implement the API call to save the delivery date
      await apiService.post(
        "/concrete-casting-registration-managment/delivery/",
        {},
        { id: selectedDocumentId, delivery_date: selectedDate.format("YYYY-MM-DD") },
      )

      handleSetNotification("Data di consegna salvata con successo", "success")
      handleCloseDeliveryDialog()
      fetchDocument() // Refresh the data
    } catch (error) {
      handleSetNotification("Errore durante il salvataggio della data di consegna", "error")
    }
  }

  function handleDeleteDeliveryDate(id) {
    if (window.confirm("Sei sicuro di voler eliminare questa consegna?\n Attenzione questa operazione è irreversibile.")) {
      deleteResource(id,'/concrete-casting-registration-managment/delivery/')
        .then((result) => {
          handleSetNotification("Consegna eliminato con successo", "success")
          fetchDocument()
        })
        .catch((error) => {
          // Gestisci l'errore se non è stato gestito nella funzione
          handleSetNotification(
            "Si è verificato un errore durante l'eliminazione della consegna. Riprova più tardi se l'errore persiste contatta il supporto tecnico indicato l'errore #00032",
            "error",
          )
        })
    }
  }

  const columns = [
    columnHelper.accessor("first_expiration", {
      header: "Prima scadenza",
      size: 80,
      enableSorting: false,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      Cell: ({ cell }) => <ExpirationChip date={cell.getValue()} />,
    }),
    columnHelper.accessor("second_expiration", {
      header: "Seconda scadenza",
      size: 80,
      enableSorting: false,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      Cell: ({ cell }) => <ExpirationChip date={cell.getValue()} />,
    }),
    columnHelper.accessor("delivery_date", {
      header: "Consegnato",
      size: 80,
      enableSorting: false,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      Cell: ({ cell, row }) => {
        const deliveryDate = cell.getValue()

        if (!deliveryDate && authContext?.hasAuth(`DELIVERED_CONCRETECASTINGREGISTRATION`)) {
          return (
            <center>
            <Tooltip title="Segna come consegnato">
              <MDButton
                onClick={() => handleOpenDeliveryDialog(row.original.id)}
                size="medium"
                variant="outlined"
                sx={{
                  minWidth: "auto",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  color: "success.main",
                  borderColor: "success.main",
                  "&:hover": {
                    backgroundColor: "success.main",
                    color: "white!important",
                  },
                }}
              >
                <CalendarTodayOutlinedIcon sx={{ fontSize: 20 }} />
              </MDButton>
            </Tooltip>
            </center>
          )
        }

        if (deliveryDate) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={deliveryDate}
                size="small"
                sx={{
                  backgroundColor: "white",
                  color: "text.primary",
                  fontWeight: 500,
                  borderRadius: "6px",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                }}
              />
              {authContext?.hasAuth(`DELIVERED_CONCRETECASTINGREGISTRATION`) && (
                <Tooltip title="Elimina data di consegna">
                  <IconButton
                    onClick={() => handleDeleteDeliveryDate(row.original.id)}
                    size="small"
                    sx={{
                      color: "error.main",
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "error.lighter",
                      },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )
        }

        return null
      },
    }),
    columnHelper.accessor("name", {
      header: "Nome getto",
      size: 80,
      enableSorting: true,
      enableColumnFilterModes: true,
    }),
    columnHelper.accessor("date", {
      header: "Prelevato il",
      size: 80,
      enableSorting: true,
      enableColumnFilterModes: true,
    }),
    columnHelper.accessor("processing_name", {
      header: "Nome processo",
      size: 100,
      enableSorting: true,
      enableColumnFilterModes: true,
    }),
    columnHelper.accessor("mq2_of_casting", {
      header: "MQ2 di getto",
      size: 50,
      enableSorting: true,
      enableColumnFilterModes: true,
    }),
    columnHelper.accessor("concrete_type", {
      header: "Tipo calcestruzzo",
      size: 100,
      enableSorting: true,
      enableColumnFilterModes: true,
    }),
    columnHelper.accessor("report_number", {
      header: "Numero report",
      size: 100,
      enableSorting: true,
      enableColumnFilterModes: true,
    }),
    columnHelper.accessor("dl_name", {
      header: "Nome DL",
      size: 100,
      enableSorting: true,
      enableColumnFilterModes: true,
    }),
    columnHelper.accessor("cubes", {
      header: "Numero Cubi",
      size: 50,
      enableSorting: true,
      enableColumnFilterModes: true,
      Cell: ({ cell }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "space-between",
            padding: 1.5,
            borderRadius: 2,
            width: "50%",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
            {cell.getValue()}
          </Typography>
          <IconButton
            onClick={() => navigate(`/concrete-cubes/selected/${cell.row.original.id}/`)}
            aria-label="View details"
            sx={{
              color: "black",
              transition: "color 0.2s ease-in-out",
              "&:hover": { color: "#0c40c6" },
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Box>
      ),
    }),
  ]

  async function deleteResource(id, endpoint) {
    try {

      // Parametri opzionali per la richiesta DELETE
      const params = { id: id } // Puoi aggiungere parametri se necessario
      const headers = {} // Headers opzionali

      // Esegui la chiamata DELETE
      const response = await apiService.delete(endpoint, params, headers)

      // Se arriviamo qui, la chiamata è andata a buon fine
      console.log(`Risorsa con id ${id} eliminata con successo:`, response)

      // Puoi fare qualcosa con la risposta, se necessario
      return response
    } catch (error) {
      // Gestisci eventuali errori
      console.error(`Errore durante l'eliminazione della risorsa con id ${id}:`, error.message)

      // Puoi gestire l'errore in modo specifico, ad esempio:
      if (error.message.includes("404")) {
        console.log(`La risorsa con id ${id} non è stata trovata`)
      } else if (error.message.includes("403")) {
        console.log(`Non hai i permessi per eliminare la risorsa con id ${id}`)
      }

      // Rilancia l'errore se vuoi che venga gestito a un livello superiore
      throw error
    }
  }

  function handleDelete(id) {
    if (window.confirm("Sei sicuro di voler eliminare questo getto?\n Attenzione questa operazione è irreversibile.")) {
      deleteResource(id,'/concrete-casting-registration-managment/')
        .then((result) => {
          handleSetNotification("getto eliminato con successo", "success")
          fetchDocument()
        })
        .catch((error) => {
          // Gestisci l'errore se non è stato gestito nella funzione
          handleSetNotification(
            "Si è verificato un errore durante l'eliminazione del getto. Riprova più tardi se l'errore persiste contatta il supporto tecnico indicato l'errore #00032",
            "error",
          )
        })
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {!loading ? (
        <>
          <center>
            <Typography variant="h4" sx={{ marginTop: "20px" }}>
              ELENCO GETTI
            </Typography>
            {authContext.hasAuth("UPDATE_CONCRETECASTINGREGISTRATION") && (
              <MDButton
                onClick={() => navigate("/concrete-casting-registration-managment/add/")}
                size="large"
                sx={{ marginTop: "20px", marginBottom: "20px" }}
              >
                {" "}
                AGGIUNGI
              </MDButton>
            )}
            <GenericTable
              data={documents}
              columns={columns}
              onEdit={onEdit}
              onDelete={handleDelete}
              authModelName="CONCRETECASTINGREGISTRATION"
            />
          </center>
        </>
      ) : (
        <center>
          <CircularProgress size={120} sx={{ marginTop: "250px", color: "#2b84ea" }} />
        </center>
      )}
      <Dialog open={openDeliveryDialog} onClose={handleCloseDeliveryDialog}>
        <DialogTitle>Seleziona la data di consegna</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
            <DatePicker
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDeliveryDialog}>Annulla</MDButton>
          <MDButton onClick={handleSaveAttachment} disabled={!selectedDate}>
            Salva
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  )
}

export default ConcreteCastingRegistrationManagment
